import { useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { FieldValues, useController, useFormContext } from 'react-hook-form';

import { ScormSelectDrawer } from 'training/forms/Field/ScormSelectorField/ScormSelectDrawer';
import {
    IScormListItemFragment,
    ScormListItemFragmentDoc,
} from 'graphql/types';
import { AddButton } from 'common/components/Button/AddButton';
import {
    getScormSelectStatus,
    isScormAdded,
    isScormSelected,
} from 'training/utils/scormSelect';
import { ScormListItem } from 'training/components/ScormListItem';
import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';

interface IProps {
    name: string;
}

export const ScormSelectorField = <TFieldValues extends FieldValues>({
    control: formControl,
    name,
}: IProps & TControllerProps<TFieldValues>) => {
    const [translate] = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedScorms, setSelectedScorms] = useState<
        IScormListItemFragment[]
    >([]);
    const client = useApolloClient();

    const control = asTypedControl<string[]>(name, formControl);

    const { setValue } = useFormContext();
    const {
        field: { value: addedScormIds },
        fieldState: { error, isTouched },
    } = useController({
        name,
        control,
    });

    const addedScorms = useMemo(() => {
        // Get scorm objects from cache based on the scorm ids set in the form
        const scorms =
            addedScormIds
                ?.map((addedScormId) => {
                    try {
                        return client.cache.readFragment<IScormListItemFragment>(
                            {
                                id: `Scorm:${addedScormId}`,
                                fragment: ScormListItemFragmentDoc,
                                fragmentName: 'ScormListItem',
                            }
                        );
                    } catch {
                        return null;
                    }
                })
                .filter(Boolean) || [];

        return scorms as IScormListItemFragment[];
    }, [addedScormIds, client.cache]);

    const handleDrawerOpen = (open: boolean) => {
        if (!open) setSelectedScorms([]);

        setDrawerOpen(open);
    };

    const handleSelectScorm = (scorm: IScormListItemFragment) => {
        const newScorms = [...selectedScorms, scorm].sort((a, b) =>
            a.title > b.title ? 1 : -1
        );

        setSelectedScorms(newScorms);
    };

    const handleRemoveScorm = (scorm: IScormListItemFragment) => {
        const { isSelected } = getScormSelectStatus(
            selectedScorms,
            addedScorms,
            scorm
        );

        if (isSelected) {
            const newSelectedScorms = selectedScorms.filter(
                (selectedScorm) => selectedScorm.id !== scorm.id
            );

            setSelectedScorms(newSelectedScorms);

            return;
        }

        setValue(
            name,
            addedScorms
                .filter((addedScorm) => addedScorm.id !== scorm.id)
                .map((scorm) => scorm.id),
            { shouldDirty: true }
        );
    };

    const handleAddScorms = () => {
        setValue(
            name,
            [
                ...(addedScormIds || []),
                ...selectedScorms.map((selectedScorm) => selectedScorm.id),
            ],
            { shouldDirty: true }
        );

        setSelectedScorms([]);

        handleDrawerOpen(false);
    };

    return (
        <Box>
            <Box
                sx={{
                    alignItems: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <AddButton
                    aria-label={translate('addScormsTitle')}
                    error={
                        !!error?.message && isTouched
                            ? error.message
                            : undefined
                    }
                    onClick={() => handleDrawerOpen(true)}
                >
                    {translate('addScormsTitle')}
                </AddButton>

                {!!addedScorms.length && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            mt: 2,
                        }}
                    >
                        {addedScorms.map((addedScorm) => (
                            <ScormListItem
                                addButton
                                added={isScormAdded(addedScorms, addedScorm)}
                                key={`added-${addedScorm.id}`}
                                scorm={addedScorm}
                                selected={isScormSelected(
                                    selectedScorms,
                                    addedScorm
                                )}
                                onClick={handleRemoveScorm}
                            />
                        ))}
                    </Box>
                )}
            </Box>

            <ScormSelectDrawer
                addedScorms={addedScorms}
                open={drawerOpen}
                selectedScorms={selectedScorms}
                onAddScorms={handleAddScorms}
                onDrawerOpen={handleDrawerOpen}
                onRemoveScorm={handleRemoveScorm}
                onSelectScorm={handleSelectScorm}
            />
        </Box>
    );
};
